import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { getRolePermissions, updateRolePermissions } from "@/lib/api";
import { PERMISSION_MATRIX } from "@/lib/sections";
import { useT } from "@/i18n/I18nContext";
import { toast } from "sonner";
import { KeyRound, Save, ShieldCheck } from "lucide-react";

const ROLE_LABEL_KEY = { manager: "perm.roleManager", qa: "perm.roleQa", operator: "perm.roleOperator" };

export default function Permissions() {
  const { t } = useT();
  const [roles, setRoles] = useState(["manager", "qa", "operator"]);
  const [perms, setPerms] = useState(null); // { manager: Set, qa: Set, operator: Set }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getRolePermissions();
      setRoles(data.roles || ["manager", "qa", "operator"]);
      const next = {};
      (data.roles || []).forEach((r) => { next[r] = new Set(data.permissions?.[r] || []); });
      setPerms(next);
    } catch {
      toast.error(t("perm.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (role, key) => {
    setPerms((prev) => {
      const next = { ...prev };
      const set = new Set(next[role]);
      if (set.has(key)) set.delete(key); else set.add(key);
      next[role] = set;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all(roles.map((r) => updateRolePermissions(r, [...(perms[r] || [])])));
      toast.success(t("perm.savedToast"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("perm.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const allSections = useMemo(() => PERMISSION_MATRIX.flatMap((g) => g.items.map((i) => i.key)), []);
  const grantAll = (role, on) => {
    setPerms((prev) => ({ ...prev, [role]: on ? new Set(allSections) : new Set() }));
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="permissions-page">
        <Card>
          <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <KeyRound className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("perm.title")}
              </CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">{t("perm.subtitle")}</p>
            </div>
            <Button onClick={save} disabled={saving || loading} data-testid="perm-save-button">
              <Save className="mr-2 h-4 w-4" /> {saving ? t("perm.saving") : t("perm.save")}
            </Button>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-border bg-secondary/50 px-3 py-2 text-xs text-muted-foreground">
              <ShieldCheck className="h-4 w-4 text-[hsl(var(--brand))]" />
              {t("perm.adminNote")}
            </div>

            {loading || !perms ? (
              <div className="space-y-3">
                {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="py-2 pr-4 text-left font-semibold">{t("perm.sectionCol")}</th>
                      {roles.map((r) => (
                        <th key={r} className="px-3 py-2 text-center font-semibold">
                          <div className="capitalize">{t(ROLE_LABEL_KEY[r] || r)}</div>
                          <div className="mt-1 flex justify-center gap-2 text-[11px] font-normal text-muted-foreground">
                            <button type="button" className="hover:text-[hsl(var(--brand))]" onClick={() => grantAll(r, true)} data-testid={`perm-${r}-all`}>{t("perm.all")}</button>
                            <span>·</span>
                            <button type="button" className="hover:text-[hsl(var(--brand))]" onClick={() => grantAll(r, false)} data-testid={`perm-${r}-none`}>{t("perm.none")}</button>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border/60 bg-secondary/30">
                      <td className="py-2 pr-4 font-medium text-muted-foreground" colSpan={roles.length + 1}>
                        <Badge className="badge-ok">{t("perm.dashboard")}</Badge>
                        <span className="ml-2 text-xs text-muted-foreground">{t("perm.alwaysOn")}</span>
                      </td>
                    </tr>
                    {PERMISSION_MATRIX.map((group) => (
                      <React.Fragment key={group.id}>
                        <tr className="bg-secondary/40">
                          <td className="px-1 py-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground" colSpan={roles.length + 1}>
                            {t(group.labelKey)}
                          </td>
                        </tr>
                        {group.items.map((item) => (
                          <tr key={item.key} className="border-b border-border/50 hover:bg-secondary/30">
                            <td className="py-2.5 pr-4">{t(item.labelKey)}</td>
                            {roles.map((r) => (
                              <td key={r} className="px-3 py-2.5 text-center">
                                <div className="flex justify-center">
                                  <Switch
                                    checked={perms[r]?.has(item.key) || false}
                                    onCheckedChange={() => toggle(r, item.key)}
                                    data-testid={`perm-${r}-${item.key}`}
                                    aria-label={`${t(ROLE_LABEL_KEY[r] || r)} - ${t(item.labelKey)}`}
                                  />
                                </div>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
